// Shared fee calculations for the Scale of Fees calculators (public, member and admin pages).
// All figures come from the NIS FCT Branch Professional Scale of Fees 2026.

export const DEPOSIT_RATE = 1; // Mandatory Deposit Scheme: 100% of the survey fee

const fmt = (v) => '₦' + Number(v).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (v) => Math.round(v * 100) / 100;

/** Upper bound of a band label such as "701 – 1,050 m²", "96 – 100" or "501 and above". */
export function parseUpperBound(label) {
  const text = String(label || '');
  if (/above|and over|\+/i.test(text)) return Infinity;
  const nums = text.replace(/,/g, '').match(/\d+(\.\d+)?/g);
  if (!nums) return null;
  return parseFloat(nums[nums.length - 1]);
}

export function toSquareMetres(value, unit) {
  const v = parseFloat(value);
  if (!v || v <= 0 || Number.isNaN(v)) return null;
  return unit === 'ha' ? v * 10000 : v;
}

export function formatArea(m2) {
  return m2 >= 10000 ? `${round2(m2 / 10000)} Ha` : `${round2(m2).toLocaleString('en-NG')} m²`;
}

// ─── PERIMETER (PER-HECTARE BANDS) ───────────────────────────────────────────
// Rows flagged per_ha hold the rate per hectare for the band ending at ha_to.
// Fee = cumulative cost of all earlier bands + (hectares inside this band × rate),
// plus the excess rate for every hectare above the last band (50 Ha).

export function getHaBounds(rows, idx) {
  let haPrev = 0;
  for (let i = 0; i < idx; i++) if (rows[i].per_ha) haPrev = Number(rows[i].ha_to) || haPrev;
  const haMax = Number(rows[idx].ha_to) || haPrev;
  const isLast = !rows.slice(idx + 1).some((r) => r.per_ha);
  const minHa = haPrev === 0 ? haMax : round2(haPrev + 0.1);
  return { haPrev, haMax, isLast, minHa };
}

export function computePerHaFee(rows, idx, cat, hectaresInput, excessRates) {
  const row = rows[idx];
  const { haPrev, haMax, isLast, minHa } = getHaBounds(rows, idx);
  let ha = parseFloat(hectaresInput);
  if (!hectaresInput || Number.isNaN(ha)) ha = haMax;
  if (ha < minHa) ha = minHa;
  if (!isLast && ha > haMax) ha = haMax;
  ha = round2(ha);
  let base = 0;
  let p = 0;
  for (let i = 0; i < idx; i++) {
    const r = rows[i];
    if (!r.per_ha) continue;
    base += (Number(r.ha_to) - p) * Number(r[cat]);
    p = Number(r.ha_to);
  }
  const rate = Number(row[cat]);
  const within = round2(Math.min(ha, haMax) - haPrev);
  const excessHa = round2(Math.max(0, ha - haMax));
  const excessRate = Number((excessRates || {})[cat] || 0);
  const fee = base + within * rate + excessHa * excessRate;
  return {
    fee, deposit: fee * DEPOSIT_RATE, cat, perHa: true, ha, rate, base, haPrev, haMax, within, excessHa, excessRate,
    band: row.size, size: `${ha} Ha (${row.size})`,
  };
}

/** "Other" perimeter entry: pick the band automatically from a manually entered area. */
export function perimeterFeeForArea(rows, cat, value, unit, excessRates) {
  const m2 = toSquareMetres(value, unit);
  if (!m2 || !cat) return null;
  const indexed = rows.map((r, i) => ({ r, i }));
  const flat = indexed.filter((x) => !x.r.per_ha);
  const perHa = indexed.filter((x) => x.r.per_ha);
  if (m2 < 10000) {
    const match = flat.find((x) => m2 <= (parseUpperBound(x.r.size) ?? -1));
    if (match) {
      const fee = Number(match.r[cat]);
      return { fee, deposit: fee * DEPOSIT_RATE, cat, size: `${formatArea(m2)} (${match.r.size})`, manual: true };
    }
  }
  if (!perHa.length) return null;
  const ha = m2 / 10000;
  const target = perHa.find((x) => ha <= Number(x.r.ha_to)) || perHa[perHa.length - 1];
  return { ...computePerHaFee(rows, target.i, cat, String(ha), excessRates), manual: true };
}

// ─── LAYOUT ──────────────────────────────────────────────────────────────────
/** "Other" layout entry: exact plot count; above the last band the per-plot excess rate applies. */
export function layoutFeeForPlots(rows, cat, value, excessRates) {
  const n = parseInt(value, 10);
  if (!n || n < 1 || !cat) return null;
  const bands = rows.filter((r) => parseUpperBound(r.plots) !== null);
  if (!bands.length) return null;
  const finiteMax = Math.max(...bands.map((r) => parseUpperBound(r.plots)).filter(Number.isFinite));
  let row = bands.find((r) => n <= parseUpperBound(r.plots));
  let extraPlots = 0;
  let extraRate = 0;
  if (!row) {
    row = bands[bands.length - 1];
    extraPlots = n - finiteMax;
    extraRate = Number((excessRates || {})[cat] || 0);
  }
  const costPerPlot = Number(row[cat]);
  const basePlots = n - extraPlots;
  const totalCost = basePlots * costPerPlot + extraPlots * extraRate;
  const note = extraPlots > 0
    ? `${basePlots} plots × ${fmt(costPerPlot)} + ${extraPlots} plots × ${fmt(extraRate)} (above ${finiteMax} plots)`
    : `${n} plots × ${fmt(costPerPlot)} (band ${row.plots})`;
  return {
    costPerPlot, totalCost, deposit: totalCost * DEPOSIT_RATE, plots: row.plots, cat, qty: n,
    extraPlots, extraRate, note, manual: true,
  };
}

// ─── AS-BUILT / DETAIL ───────────────────────────────────────────────────────
export function asbuiltFeeForPlots(rows, cat, value) {
  const n = parseInt(value, 10);
  if (!n || n < 1 || !cat) return null;
  const bands = rows.filter((r) => parseUpperBound(r.plots) !== null);
  if (!bands.length) return null;
  const row = bands.find((r) => n <= parseUpperBound(r.plots)) || bands[bands.length - 1];
  const costPerPlot = Number(row[cat]);
  const totalCost = n * costPerPlot;
  return {
    costPerPlot, totalCost, deposit: totalCost * DEPOSIT_RATE, plots: row.plots, cat, qty: n,
    note: `${n} plots × ${fmt(costPerPlot)}`, manual: true,
  };
}

// ─── TOPOGRAPHICAL ───────────────────────────────────────────────────────────
/** "Other" topographical entry using the schedule's alternative procedure:
 *  up to 1,000 m² = first row, up to 1 Ha = second row, every further hectare = first-row fee again. */
export function topoFeeForArea(rows, cat, value, unit) {
  const m2 = toSquareMetres(value, unit);
  if (!m2 || !cat || rows.length < 2) return null;
  const r1 = rows[0];
  const r2 = rows[1];
  const f1 = Number(r1[cat]);
  const f2 = Number(r2[cat]);
  if (m2 <= 1000) {
    return { fee: f1, deposit: f1 * DEPOSIT_RATE, cat, size: `${formatArea(m2)} (up to 1,000 m²)`, manual: true };
  }
  if (m2 <= 10000) {
    return { fee: f2, deposit: f2 * DEPOSIT_RATE, cat, size: `${formatArea(m2)} (up to 1 Ha)`, manual: true };
  }
  const extraHa = Math.ceil(round2((m2 - 10000) / 10000));
  const fee = f2 + extraHa * f1;
  return {
    fee, deposit: fee * DEPOSIT_RATE, cat, size: `${formatArea(m2)}`, manual: true,
    note: `1 Ha ${fmt(f2)} + ${extraHa} Ha × ${fmt(f1)} (alternative procedure)`,
  };
}
